import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AlertCircle, Loader, RefreshCw } from 'lucide-react';
import { checkSupabaseConnection } from './lib/supabase';
import { Sidebar } from './components/Sidebar';
import { Home } from './pages/Home';
import { Artists } from './pages/Artists';
import { Settings } from './pages/Settings';
import { AdCreatives } from './pages/AdCreatives';
import { Archive } from './pages/Archive';
import { FileStorage } from './pages/FileStorage';
import { PublicFileUpload } from './pages/PublicFileUpload';
import { ContentPlan } from './pages/ContentPlan';
import { PublicArtistView } from './pages/PublicArtistView';
import { ArtistAdCreatives } from './pages/ArtistAdCreatives';
import { ArtistContentPlan } from './pages/ArtistContentPlan';
import { ArtistReleaseSheets } from './pages/ArtistReleaseSheets';
import { ArtistIdeas } from './pages/ArtistIdeas';
import { ReleaseSheetEditor } from './pages/ReleaseSheetEditor';
import { ReleaseSheets } from './pages/ReleaseSheets';
import Releases from './pages/Releases';
import Dashboard from './pages/Dashboard';
import { PreviewArtistView } from './pages/PreviewArtistView';
import { Library } from './pages/Library';
import { Login } from './pages/Login';
import { Signup } from './pages/Signup';
import { PushNotifications } from './pages/PushNotifications';
import { NovelTest } from './pages/NovelTest';
import NewCampaignSubmission from './pages/NewCampaignSubmission';
import { ComponentLibrary } from './pages/ComponentLibrary';
import { URLShortener } from './pages/URLShortener';
import { Logs } from './pages/Logs';
import MetaCampaigns from './pages/MetaCampaigns';
import { MasterclassAdmin } from './pages/MasterclassAdmin';
import { TourDates } from './pages/TourDates';
import { MasterclassPublic } from './pages/MasterclassPublic';
import { Redirect } from './pages/Redirect';
import { NotFound } from './pages/NotFound';
import { DynamicShortUrlRoutes } from './components/DynamicShortUrlRoutes';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AuthProvider } from './contexts/AuthContext';
import { PrivateRoute } from './components/PrivateRoute';
import { ArtistAuthGuard } from './components/ArtistAuthGuard';
import { ArtistClientProvider } from './contexts/ArtistClientContext';
import { useDarkMode } from './hooks/useDarkMode';
import { SnowEffect } from './components/SnowEffect';
import { TopBar } from './components/TopBar';
import JanaChat from './pages/JanaChat';
import { UGC } from './pages/UGC';
import './styles/snow.css';


function App() {
  const [appInitialized, setAppInitialized] = useState(false);
  const [connectionError, setConnectionError] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  useDarkMode();

  useEffect(() => {
    // Skip global data fetching - let each component load its own data
    console.log('🚀 App initialization - skipping global data fetch');
    setAppInitialized(true);
  }, []);

  // Show loading screen while app initializes
  if (!appInitialized && !connectionError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <Loader className="h-12 w-12 animate-spin text-primary-500 mx-auto mb-4" />
          <p className="text-white text-lg">Loading VideoFeedback...</p>
        </div>
      </div>
    );
  }

  // Show connection error
  const handleRetry = async () => {
    setIsReconnecting(true);
    try {
      const isConnected = await checkSupabaseConnection();
      if (isConnected) {
        setConnectionError(false);
        setAppInitialized(true);
      }
    } catch (error) {
      console.error('Retry failed:', error);
    } finally {
      setIsReconnecting(false);
    }
  };

  if (connectionError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 p-4">
        <div className="bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full">
          <div className="flex items-center gap-3 text-red-400 mb-4">
            <AlertCircle className="h-6 w-6" />
            <h1 className="text-xl font-bold">Connection Error</h1>
          </div>
          <p className="text-gray-300 mb-6">
            Unable to connect to the server. Please check your internet connection and try again.
          </p>
          <button
            onClick={handleRetry}
            disabled={isReconnecting}
            className="w-full px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isReconnecting ? (
              <>
                <Loader className="animate-spin h-4 w-4" />
                Reconnecting...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />
                Retry Connection
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  // Check if it's Christmas time (December)
  const isChristmasTime = new Date().getMonth() === 11;

  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          {isChristmasTime && <SnowEffect />}
          <div className="min-h-screen bg-gray-50 dark:bg-[#000000] text-gray-900 dark:text-dark-100">
            <Routes>
              {/* Public routes */}
              <Route path="/404" element={<NotFound />} />
              <Route path="/redirect/:shortCode" element={<Redirect />} />
              
              {/* Dynamic short URL routes - only for swipe.fm */}
              {window.location.hostname === 'swipe.fm' && <Route path="/*" element={<DynamicShortUrlRoutes />} />}
              
              <Route path="/artist/:id" element={<ArtistAuthGuard><ArtistClientProvider><PublicArtistView /></ArtistClientProvider></ArtistAuthGuard>} />
              <Route path="/artist/:id/ad-creatives" element={<ArtistAuthGuard><ArtistClientProvider><ArtistAdCreatives /></ArtistClientProvider></ArtistAuthGuard>} />
              <Route path="/artist/:id/content-plan" element={<ArtistAuthGuard><ArtistClientProvider><ArtistContentPlan /></ArtistClientProvider></ArtistAuthGuard>} />
              <Route path="/artist/:id/release-sheets" element={<ArtistAuthGuard><ArtistClientProvider><ArtistReleaseSheets /></ArtistClientProvider></ArtistAuthGuard>} />
              <Route path="/artist/:id/ideas" element={<ArtistClientProvider><ArtistIdeas /></ArtistClientProvider>} />
              <Route path="/artist/:id/masterclass" element={<ArtistAuthGuard><ArtistClientProvider><MasterclassPublic /></ArtistClientProvider></ArtistAuthGuard>} />
              <Route path="/artist/:id/release-sheets/:sheetId" element={<ArtistAuthGuard><ArtistClientProvider><ReleaseSheetEditor /></ArtistClientProvider></ArtistAuthGuard>} />
              <Route path="/artist/:id/release-sheets/:sheetId/edit" element={<ArtistAuthGuard><ArtistClientProvider><ReleaseSheetEditor /></ArtistClientProvider></ArtistAuthGuard>} />
              <Route path="/release-sheets/templates/:templateId/edit" element={<ReleaseSheetEditor />} />
              <Route path="/release-sheets/components" element={<ComponentLibrary />} />
              <Route path="/novel-test" element={<NovelTest />} />
              <Route path="/preview/:id" element={<PreviewArtistView />} />
              <Route path="/new-campaign" element={<NewCampaignSubmission />} />
              <Route path="/campaigns" element={<MetaCampaigns />} />
              <Route path="/share/:token" element={<PublicFileUpload />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              
              {/* Library route - always public, no authentication required */}
              <Route
                path="/library"
                element={
                  <div className="flex w-full h-screen">
                    <Sidebar />
                    <main className="flex-1 overflow-auto" style={{ backgroundColor: '#111111' }}>
                      <Library />
                    </main>
                  </div>
                }
              />
              
              {/* Protected routes */}
              <Route
                path="/*"
                element={
                  <PrivateRoute>
                    <div className="flex w-full h-screen">
                      <Sidebar />
                      <main className="flex-1 overflow-auto flex flex-col" style={{ backgroundColor: '#111111' }}>
                        <TopBar />
                        <div className="w-full flex-1 overflow-auto">
                          <Routes>
                            <Route index element={<Navigate to="/dashboard" replace />} />
                            <Route path="dashboard" element={<Dashboard />} />
                            <Route path="videos" element={<Home />} />
                            <Route path="ad-creatives" element={<AdCreatives />} />
                            <Route path="content-plan" element={<ContentPlan />} />
                            <Route path="release-sheets" element={<ReleaseSheets />} />
                            <Route path="releases" element={<Releases />} />
                            <Route path="artists" element={<Artists />} />
                            <Route path="tour" element={<TourDates />} />
                            <Route path="whatsapp" element={<Navigate to="/logs" replace />} />
                            <Route path="archive" element={<Archive />} />
                            <Route path="file-storage" element={<FileStorage />} />
                            <Route path="settings" element={<Settings />} />
                            <Route path="notifications" element={<PushNotifications />} />
                            <Route path="url-shortener" element={<URLShortener />} />
                            <Route path="logs" element={<Logs />} />
                            <Route path="masterclass" element={<MasterclassAdmin />} />
                            <Route path="jana" element={<JanaChat />} />
                            <Route path="ugc" element={<UGC />} />
                            <Route path="*" element={<Navigate to="/dashboard" replace />} />
                          </Routes>
                        </div>
                      </main>
                    </div>
                  </PrivateRoute>
                }
              />
            </Routes>
          </div>
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;